import { get } from 'lodash';
import i18next from 'i18next';

import normalizeStringValue from '@commons/utils/normalizeStringValue';

const SHEET = {
  ID: 'centralKitchenProducts', // Must match the sheet ID declared in the constant on the server
  NAME: 'Produits_Centrale',
};

// The "key" property will be the keys used for payload, must be similar to COLUMN_CONFIG keys
// definition on the server.
// "normalizedKey" are the translated headers as generated by the server, normalized (without
// diacritics and lower cased), so the mapping stays in sync with the user language.
const getColumnIds = () => ({
  NAME: {
    key: 'name',
    normalizedKey: normalizeStringValue(i18next.t('_BACKEND_TRANSLATIONS.PRODUCTS.IMPORTS.NAME')),
  },
  CATEGORY: {
    key: 'category',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.PRODUCTS.IMPORTS.CATEGORY'),
    ),
  },
  SUB_CATEGORY: {
    key: 'subCategory',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.PRODUCTS.IMPORTS.SUB_CATEGORY'),
    ),
  },
  SKU: {
    key: 'sku',
    normalizedKey: normalizeStringValue(i18next.t('_BACKEND_TRANSLATIONS.PRODUCTS.IMPORTS.SKU')),
    mappingFn: (row, normalizedKey) => get(row, normalizedKey, '').toString(),
  },
  SHELF_LIFE: {
    key: 'shelfLife',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.PRODUCTS.IMPORTS.SHELF_LIFE'),
    ),
  },
  PRICE: {
    key: 'price',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.PRICE'),
    ),
  },
  VAT_RATE: {
    key: 'vatRate',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.VAT_RATE'),
    ),
  },
  COMPOSITION_TYPE: {
    key: 'compositionTypes',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.PRODUCTS.IMPORTS.COMPOSITION_TYPE'),
    ),
  },
  PRODUCT_ASSOCIATED_RECIPE: {
    key: 'associatedRecipe',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.CENTRAL_KITCHEN_PRODUCTS.IMPORTS.ASSOCIATED_RECIPE'),
    ),
  },
  PRODUCT_ASSOCIATED_INGREDIENT: {
    key: 'associatedIngredient',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.CENTRAL_KITCHEN_PRODUCTS.IMPORTS.ASSOCIATED_INGREDIENT'),
    ),
  },
  PORTION_SIZE_IN_GRAMS: {
    key: 'portionSizeInGrams',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.PRODUCTS.IMPORTS.PORTION_SIZE_IN_GRAMS'),
    ),
  },
  SUPPLIER_PRODUCT_ENTITY: {
    key: 'supplierProductEntity',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.CENTRAL_KITCHEN_PRODUCTS.IMPORTS.SUPPLIER_PRODUCT_ENTITY'),
    ),
  },
  LOSS: {
    key: 'loss',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.LOSS'),
    ),
  },
  PACKAGE_WEIGHT: {
    key: 'packageWeight',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.PACKAGE_WEIGHT'),
    ),
  },
  INVOICE_PACKAGING: {
    key: 'invoicePackagingName',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.INVOICE_PACKAGING'),
    ),
  },
  ORDER_PACKAGING: {
    key: 'orderPackagingName',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.ORDER_PACKAGING'),
    ),
  },
  DELIVERY_PACKAGING: {
    key: 'receptionPackagingName',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.DELIVERY_PACKAGING'),
    ),
  },
  PACKAGING_NAME_1: {
    key: 'packagingName1',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.PACKAGING_NAME_1'),
    ),
  },
  PACKAGING_NAME_2: {
    key: 'packagingName2',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.PACKAGING_NAME_2'),
    ),
  },
  PACKAGING_NAME_3: {
    key: 'packagingName3',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.PACKAGING_NAME_3'),
    ),
  },
  PACKAGING_UNIT_1: {
    key: 'packagingUnit1',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.PACKAGING_UNIT_1'),
    ),
  },
  PACKAGING_UNIT_2: {
    key: 'packagingUnit2',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.PACKAGING_UNIT_2'),
    ),
  },
  PACKAGING_UNIT_3: {
    key: 'packagingUnit3',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.PACKAGING_UNIT_3'),
    ),
  },
  PACKAGING_QUANTITY_1: {
    key: 'packagingQuantity1',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.PACKAGING_QUANTITY_1'),
    ),
  },
  PACKAGING_QUANTITY_2: {
    key: 'packagingQuantity2',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.PACKAGING_QUANTITY_2'),
    ),
  },
  PACKAGING_QUANTITY_3: {
    key: 'packagingQuantity3',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.SUPPLIER_PRODUCTS.IMPORTS.PACKAGING_QUANTITY_3'),
    ),
  },
});

// Central Kitchen Product associated ingredient sheet definition
const HIDDEN_SHEET_ASSOCIATED_INGREDIENT = {
  ID: 'associatedIngredient',
  NAME: 'ingrédientAssocié',
};

const HIDDEN_ASSOCIATED_INGREDIENT_COLUMN_IDS = {
  ID: 'id',
  NAME: 'name',
  UNIT: 'unit',
};

const HIDDEN_ASSOCIATED_INGREDIENT_NORMALIZE_COLUMN_IDS = {
  ID: 'ingredientid',
  NAME: 'nom',
  UNIT: 'unite',
};

// Central Kitchen Product associated ingredient sheet definition
const HIDDEN_SHEET_ASSOCIATED_RECIPE = {
  ID: 'associatedRecipe',
  NAME: 'recetteAssociée',
};

const HIDDEN_ASSOCIATED_RECIPE_COLUMN_IDS = {
  ID: 'id',
  NAME: 'name',
  UNIT: 'unit',
};

const HIDDEN_ASSOCIATED_RECIPE_NORMALIZE_COLUMN_IDS = {
  ID: 'recipeid',
  NAME: 'nom',
  UNIT: 'unite',
};

export default {
  SHEET,
  getColumnIds,
  // Central Kitchen Product associated recipe
  HIDDEN_SHEET_ASSOCIATED_RECIPE,
  HIDDEN_ASSOCIATED_RECIPE_COLUMN_IDS,
  HIDDEN_ASSOCIATED_RECIPE_NORMALIZE_COLUMN_IDS,
  // Central Kitchen Product associated ingredient
  HIDDEN_SHEET_ASSOCIATED_INGREDIENT,
  HIDDEN_ASSOCIATED_INGREDIENT_COLUMN_IDS,
  HIDDEN_ASSOCIATED_INGREDIENT_NORMALIZE_COLUMN_IDS,
};
